import React from 'react';
import { Clock, RotateCcw } from 'lucide-react';
import { buildTenderBreakdown } from '../../utils/paiementTenderBreakdown';
import { getCommandeFinancier } from '../../utils/commandeFinanceDisplay';
import { formatEuro } from '../../utils/rdvPaymentDisplay';

function StripeBadgeIcon() {
  return (
    <svg className="h-3.5" viewBox="0 0 468 222" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <g fill="#635BFF">
        <path d="M414 113.4c0-25.6-12.4-45.8-36.1-45.8-23.8 0-38.2 20.2-38.2 45.6 0 30.1 17 45.3 41.4 45.3 11.9 0 20.8-2.7 27.7-6.5V136c-6.8 3.4-14.6 5.5-24.5 5.5-9.7 0-18.3-3.4-19.4-15.2h48.9c0-1.3.2-6.5.2-13.9zm-49.4-9.2c0-11.3 6.9-16 13.2-16 6.1 0 12.6 4.7 12.6 16h-25.8z" />
        <path d="M301.1 67.6c-9.8 0-16.1 4.6-19.6 7.8l-1.3-6.2h-22.4v113.3l25-5.3.1-29.6c3.6 2.6 8.9 6.3 18.7 6.3 17.9 0 34.2-10.3 34.2-39.2.1-29-15.6-47.3-33.7-47.3zm-6 57.3c-5.9 0-9.4-2.1-11.8-4.7l-.1-37.1c2.6-2.9 6.2-4.9 11.9-4.9 9.1 0 15.4 10.6 15.4 23.3 0 12.6-6.2 23.4-15.4 23.4z" />
        <path d="M223.3 61.4l25.1-5.4V36.4l-25.1 5.3v19.7zm.1 55.8l.1-47.7 25-5.4.1 47.7-25.2 5.4z" />
        <path d="M196.9 76.7l-24.4 5.2-.1 40.8c0 14.8 9.1 18.1 17.3 18.1 5.2 0 10.4-.6 15.2-2.1l2-19.8c-3.6 1.2-7.3 1.8-10.1 1.8-4.8 0-7.4-2.1-7.4-6.7l-.1-37.3h27.6v-1.8z" />
        <path d="M146.9 47.6l-24.7 5.3v72.4h25V47.6z" />
        <path d="M79.3 94.7c0-3.9 3.2-5.4 8.5-5.4 7.6 0 17.2 2.3 24.8 6.4V72.2c-8.3-3.3-16.5-4.6-24.8-4.6C67.5 67.6 54 78.2 54 95.9c0 27.6 38 23.2 38 35.1 0 4.6-4 6.1-9.6 6.1-8.3 0-18.9-3.4-27.3-8v23.8c9.3 4 18.7 5.7 27.3 5.7 20.8 0 35.1-10.3 35.1-28.2-.1-29.8-38.2-24.5-38.2-35.7z" />
      </g>
    </svg>
  );
}

/**
 * Cellule paiement liste Mes commandes (produit) — alignée commerçant / Super Admin.
 */
export default function MesCommandesListPaiementCell({ commande, isPaiementStripe }) {
  const fin = getCommandeFinancier(commande);
  const tenders = buildTenderBreakdown(commande?.paiements || [], {
    modePaiement: commande?.modePaiement,
  });
  const isPaye = commande.statutPaiement === 'VALIDE' || commande.statutPaiement === 'PAYE';
  const stripe = tenders.hasStripe || isPaiementStripe(commande);
  const giftOnly = tenders.hasGift && !tenders.hasStripe;

  if ((stripe || giftOnly || tenders.isMixed) && fin.isRembourseTotal) {
    return (
      <div className="flex flex-col gap-0.5 bg-orange-50 border border-orange-200 rounded-lg px-2 py-1">
        <div className="flex items-center gap-1.5">
          <RotateCcw className="h-3.5 w-3.5 text-orange-700 flex-shrink-0" />
          {tenders.hasStripe ? <StripeBadgeIcon /> : null}
          <span className="text-xs font-medium text-orange-800">Remboursé</span>
        </div>
      </div>
    );
  }

  if ((stripe || giftOnly || tenders.isMixed) && fin.hasResteAEncaisser) {
    return (
      <div className="flex flex-col gap-0.5 bg-sky-50 border border-sky-200 rounded-lg px-2 py-1">
        <span className="text-xs font-medium text-sky-900">Complément dû</span>
        <span className="text-xs text-sky-800 pl-1">Reste : {formatEuro(fin.resteAEncaisser)}</span>
      </div>
    );
  }

  if ((stripe || giftOnly || tenders.isMixed) && fin.isPartiellementRembourse) {
    return (
      <div className="flex flex-col gap-0.5 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
        <span className="text-xs font-medium text-amber-900">Partiellement remboursé</span>
        <span className="text-xs text-amber-800 pl-1">Net encaissé : {formatEuro(fin.netEncaisse)}</span>
      </div>
    );
  }

  if (isPaye && tenders.lines.length > 0) {
    return (
      <div className="flex flex-col gap-0.5 bg-green-50 border border-green-200 rounded-lg px-2 py-1 min-w-[7rem]">
        {tenders.lines.map((l) => (
          <div key={l.methode} className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-green-800">{l.label}</span>
            <span className="text-xs text-green-900 tabular-nums">{formatEuro(l.amount)}</span>
          </div>
        ))}
      </div>
    );
  }

  if (stripe && commande.statutPaiement === 'EN_ATTENTE') {
    return (
      <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1">
        <Clock className="h-4 w-4 text-blue-600 flex-shrink-0" />
        <span className="text-xs font-medium text-blue-700">Paiement en ligne</span>
        <span className="text-xs text-blue-600">(En attente)</span>
      </div>
    );
  }

  if (commande.modePaiement === 'surplace' || commande.methodePaiement === 'ESPECES') {
    return <span className="text-xs text-gray-500">Sur place</span>;
  }

  if (commande.statutPaiement === 'EN_ATTENTE') {
    return <span className="text-xs text-amber-600">En attente</span>;
  }

  return <span className="text-xs text-gray-400">—</span>;
}
